import * as React from "react";
import { debounce, camelCase } from "lodash";
import { QuestionQueryModel, getSubmissionData, Option, GetReferenceList, Question } from "../utils/ServiceManagerAPI";
import { useDarkMode } from "./DarkModeSwitch";
import SignatureOption from "./SignatureOption";
import { QuestionnaireContext } from "../routes/Questions";
import { validate as validateFunction } from "../utils/ServiceManagerAPI";

const { Component } = React;
const AnswerableOption = ({
    question,
    option,
    changed,
    value: propValue,
    atomic,
    disabled,
    style,
    onValidate,
    validate,
}: {
    question: Question;
    option: Option;
    changed: Function;
    value: any;
    atomic: boolean;
    disabled: boolean;
    style: Object;
    onValidate: Function;
    validate: boolean;
}) => {
    const [value, setValue] = React.useState("" as any);
    const [valid, setValid] = React.useState(question.dataValidations?.length == 0);

    const [referenceList, setReferenceList] = React.useState([] as any[]);
    const [referenceValue, setReferenceValue] = React.useState({} as any);

    React.useEffect(() => {
        if (option.referenceListID) {
            GetReferenceList(option.referenceListID).then((res) => {
                setReferenceList(res.data.result.items);
                setReferenceValue(res.data.result.items.find((item) => item.name == value));
            });
        }
    }, [value]);

    const questionnaire = React.useContext(QuestionnaireContext);

    const doChange = (v) => {
        doFocus(false);
        if (!changed(v)) {
            setValue(propValue);
        }
    };

    React.useEffect(() => {
        setValid(doValidation(propValue));
        if (value != propValue) {
            let v = propValue;

            if (v === null) {
                v = "";
            }

            switch (v?.toString()?.toLowerCase()) {
                case "true":
                    v = true;
                    break;
                case "false":
                    v = false;
                    break;
                case null:
                    v = "";
                    break;
            }

            setValue(v);
        }
    }, [propValue]);

    const doValidation = (v) => {
        if (validate) {
            let valid = true;
            question.dataValidations?.forEach((validation) => {
                if (!validation.optionsAppliedTo || validation.optionsAppliedTo.length == 0 || validation.optionsAppliedTo.includes(option.id)) {
                    valid = valid && validateFunction(validation, v, question, getSubmissionData(questionnaire));
                }
            });
            onValidate && onValidate(valid, option.id);
            return valid;
        }
        return true;
    };

    const handleChange = (e) => {
        let v = e.target.value;
        if (option.type == 0) {
            v = !value;
        }

        setValue(v);
        setValid(doValidation(v));
        return v;
    };

    const handleAtomicChange = (value) => (e) => {
        setValue(value);
        doChange(value);
        setValid(doValidation(value));
    };

    const handleChangeAndUpdate = (e) => {
        let value = handleChange(e);
        doChange(value);
        setValid(doValidation(value));
    };

    const handleReferenceChange = (value) => {
        setReferenceValue(value);
        doChange(value.name);
        setValid(doValidation(value));
    };

    const [isDarkMode] = useDarkMode();

    const inputStyleClass = isDarkMode ? "bg-gray-800 text-gray-100" : "bg-gray-100 text-gray-900";

    const [focus, setFocus] = React.useState(false);

    const doFocus = (focus) => {
        setFocus(focus);
    };

    React.useEffect(() => {
        if (focus) {
            window.onbeforeunload = function (evt) {
                if (propValue != value) {
                    changed(value);
                }
            };
        } else {
            window.onbeforeunload = null;
        }
    }, [focus, value, propValue]);

    switch (option.type) {
        case 0: {
            return (
                <>
                    {atomic && (
                        <div className="flex flex-col gap-1" style={style}>
                            {option.text && <span className="text-gray-800 dark:text-gray-200">{option.text}</span>}
                            <div className="flex flex-row gap-1">
                                <button
                                    disabled={disabled}
                                    onClick={handleAtomicChange(true)}
                                    className={`px-4 py-2 rounded-md transition-colors ${
                                        value == true 
                                            ? "bg-purple-600 text-white" 
                                            : disabled 
                                                ? "bg-gray-300 dark:bg-gray-700 text-gray-500" 
                                                : "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 hover:bg-purple-200"
                                    }`}
                                >
                                    Yes
                                </button>
                                <button
                                    disabled={disabled}
                                    onClick={handleAtomicChange(false)}
                                    className={`px-4 py-2 rounded-md transition-colors ${
                                        value == false 
                                            ? "bg-purple-600 text-white" 
                                            : disabled 
                                                ? "bg-gray-300 dark:bg-gray-700 text-gray-500" 
                                                : "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 hover:bg-purple-200"
                                    }`}
                                >
                                    No
                                </button>
                            </div>
                        </div>
                    )}
                    {!atomic && (
                        <button
                            disabled={disabled}
                            onClick={handleChangeAndUpdate}
                            style={{ marginBottom: "0.3rem", ...style }}
                            className={`px-4 py-2 rounded-md transition-colors ${
                                !!value 
                                    ? "bg-purple-600 text-white" 
                                    : disabled 
                                        ? "bg-gray-300 dark:bg-gray-700 text-gray-500" 
                                        : `${inputStyleClass} hover:bg-gray-200 dark:hover:bg-gray-700`
                            }`}
                        >
                            {option.text}
                        </button>
                    )}
                </>
            );
        }
        case 1:
            return (
                <div>
                    <div className="flex flex-col gap-1" style={style}>
                        {option.text && <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{option.text}</label>}
                        <input
                            type="text"
                            onFocus={() => doFocus(true)}
                            autoComplete="nope"
                            name={camelCase(option.text)}
                            value={value}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                            disabled={disabled}
                            className={`w-full min-h-[44.67px] max-h-[150px] h-[34px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${inputStyleClass}`}
                        />
                    </div>
                </div>
            );
        case 2:
            return (
                <div>
                    <div className="flex flex-col gap-1" style={style}>
                        {option.text && <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{option.text}</label>}
                        <input
                            type="number"
                            onFocus={() => doFocus(true)}
                            autoComplete="nope"
                            name={camelCase(option.text)}
                            value={value}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${inputStyleClass}`}
                        />
                    </div>
                </div>
            );
        case 4:
            return (
                <div>
                    <div className="flex flex-col gap-1" style={style}>
                        {option.text && <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{option.text}</label>}
                        <input
                            type="date"
                            onFocus={() => doFocus(true)}
                            autoComplete="nope"
                            name={camelCase(option.text)}
                            value={value}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${inputStyleClass}`}
                        />
                    </div>
                </div>
            );
        case 5:
            return (
                <div>
                    <div className="flex flex-col gap-1" style={style}>
                        {option.text && <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{option.text}</label>}
                        <input
                            type="time"
                            onFocus={() => doFocus(true)}
                            autoComplete="nope"
                            name={camelCase(option.text)}
                            value={value}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${inputStyleClass}`}
                        />
                    </div>
                </div>
            );
        case 6:
            return (
                <div>
                    <div className="flex flex-col gap-1" style={style}>
                        {option.text && <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{option.text}</label>}
                        {!referenceList && <></>}
                        {referenceList && (
                            <select
                                disabled={disabled}
                                autoComplete="nope"
                                name={camelCase(option.text)}
                                value={referenceValue?.name || ""}
                                onChange={(e) => {
                                    const selected = referenceList.find(item => item.name === e.target.value);
                                    handleReferenceChange(selected);
                                }}
                                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${inputStyleClass}`}
                            >
                                <option value="">Select...</option>
                                {referenceList.map((item) => (
                                    <option key={item.name} value={item.name}>{item.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
            );
        case 7:
            return (
                <div>
                    <div className="flex flex-col gap-1" style={style}>
                        {option.text && <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{option.text}</label>}
                        <textarea
                            onFocus={() => doFocus(true)}
                            autoComplete="nope"
                            name={camelCase(option.text)}
                            value={value}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                            disabled={disabled}
                            rows={4}
                            className={`w-full min-h-[150px] max-h-[150px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${inputStyleClass}`}
                        />
                    </div>
                </div>
            );
        case 8:
            return <SignatureOption changed={doChange} value={value} disabled={disabled}></SignatureOption>;
    }

    return <></>;
};
export default AnswerableOption;
